// eventually move SystemConversionEventDefinition to common place
import { SystemConversionEventDefinition } from 'modules/experiments/types/tableQuery';
import { UpdateExperimentsArgs } from 'modules/experiments/types/updateExperimentSettings';
import {
  CampaignConversion,
  EditExperimentZodFormValuesType,
  TestStrategy,
} from 'modules/experiments/types';
import { OriginalExperimentState } from 'modules/experiments/takeover/types';
import {
  getBlastCampaignMetaInfo,
  getHoldoutMetaInfo,
  getSendTimeExperimentTimeZone,
  getTriggerCampaignMetaInfo,
} from './experimentDetails';
import { convertTimezoneInZonedDateTime } from './date';

type GenerateUpdateExperimentSettingArgType = {
  experimentId: number;
  formValues: EditExperimentZodFormValuesType;
  campaignConversions: CampaignConversion[];
  controlStartTime: string;
  isBlastCampaign: boolean;
  isTriggerCampaign: boolean;
  isSendTimeExperiment: boolean;
};

export const isWinningCriteriaASystemEvent = (eventName: string) => {
  return Object.values(SystemConversionEventDefinition).includes(
    eventName as SystemConversionEventDefinition,
  );
};

// Update the form values to the matching schema the mutation needs
export const generateUpdateExperimentSettingArgs = ({
  experimentId,
  formValues,
  campaignConversions,
  controlStartTime,
  isBlastCampaign,
  isTriggerCampaign,
  isSendTimeExperiment,
}: GenerateUpdateExperimentSettingArgType): UpdateExperimentsArgs => {
  const {
    testSendsPerVariation,
    testSizePercentage,
    winningCriteriaName,
    testDurationMinutes = 0,
    testDurationHours = 0,
    holdOutGroupSize,
    hasHoldOutGroup,
    attributionPeriodHours,
    triggeredTestStrategy,
    testStrategy,
    sendTimeUseRecipientTimeZones,
    sendTimeStartTimeZone = '',
  } = formValues;

  const isSystemEvent = isWinningCriteriaASystemEvent(winningCriteriaName as string);
  const showTriggerSettings =
    isTriggerCampaign && triggeredTestStrategy === TestStrategy.SUBSET_USERS;
  const showBlastSetting = isBlastCampaign;
  const showHoldoutSetting = isBlastCampaign && hasHoldOutGroup;
  const testSizePercentageToggle = testStrategy === TestStrategy.SUBSET_USERS;
  const conversionEventSettings = isSystemEvent
    ? {
        systemConversionEventType: winningCriteriaName as SystemConversionEventDefinition,
      }
    : {
        customConversionId: campaignConversions.find(
          (conversion) => conversion.name === winningCriteriaName,
        )?.id,
      };

  const explorationTriggerSettings = showTriggerSettings
    ? {
        testSendsPerVariation,
      }
    : null;

  const explorationBlastSettings =
    showBlastSetting && testSizePercentageToggle
      ? {
          testSizePercentage,
          testDurationMinutes: testDurationHours * 60 + testDurationMinutes,
        }
      : null;

  const holdoutSettings = showHoldoutSetting
    ? {
        holdOutGroupSize,
        attributionPeriodHours,
      }
    : null;

  const sendTimeSettings = isSendTimeExperiment
    ? {
        sendTimeUseRecipientTimeZones,
        sendTimeStartTimeZone: convertTimezoneInZonedDateTime(
          controlStartTime,
          sendTimeStartTimeZone,
        ),
      }
    : null;

  return {
    experimentId,
    conversionEventSettings,
    explorationTriggerSettings,
    explorationBlastSettings,
    holdoutSettings,
    sendTimeSettings,
  };
};

export const getExperimentFormSettingDefaultValue = (
  data: OriginalExperimentState,
  defaultTimeZone: string,
) => {
  const {
    testStrategy: blastTestStrategy,
    testDurationHours,
    testDurationMinutes,
    testSizePercentage,
  } = getBlastCampaignMetaInfo(data);
  const { testStrategy: triggerTestStrategy, testSendsPerVariation } =
    getTriggerCampaignMetaInfo(data);
  const { attributionPeriodHours, hasHoldOutGroup, holdOutGroupSize } = getHoldoutMetaInfo(data);
  const { sendTimeStartTimeZone, sendTimeUseRecipientTimeZones } = getSendTimeExperimentTimeZone(
    data,
    defaultTimeZone,
  );

  return {
    holdOutGroupSize,
    attributionPeriodHours,
    testSizePercentage,
    hasHoldOutGroup,
    winningCriteriaName: data?.winningCriteria ? data?.winningCriteria?.name : '',
    testDurationHours,
    testDurationMinutes,
    sendTimeStartTimeZone,
    sendTimeUseRecipientTimeZones,
    testSendsPerVariation,
    triggeredTestStrategy: triggerTestStrategy,
    testStrategy: blastTestStrategy,
    experimentType: data?.type || '',
  };
};
